import type { QueryClient } from '@tanstack/react-query'

import { logger } from '@/utils/logger'

/**
 * Clear all React Query caches when the authenticated identity changes.
 *
 * Why this exists
 * - When starting impersonation (admin -> target user), any cached data from the
 *   admin session can leak into the impersonated experience during the first render
 *   if not cleared. That includes privileged endpoints like employee lists or analytics.
 * - When ending impersonation (target user -> admin), cached data from the target user
 *   can remain visible to the admin until refetch, which is misleading and unsafe.
 *
 * Guidance
 * - Always call one of these helpers immediately after tokens/state switch and before
 *   navigating to a new route or rendering views that depend on user context.
 * - Prefer clearing the entire cache rather than selective invalidation to avoid
 *   accidentally preserving privileged data.
 */

export async function resetQueryCacheOnImpersonationStart(queryClient: QueryClient): Promise<void> {
    try {
        // Stop any in-flight requests from committing admin data after identity switch
        await queryClient.cancelQueries()
        // Add microtask delay to ensure cancellation completes before clearing
        await new Promise<void>((resolve) => queueMicrotask(() => resolve()))
        queryClient.clear()
        logger.info('[RQ] Cleared cache on impersonation start')
    } catch (e) {
        // Non-blocking safeguard; failures should not break navigation
        logger.warn('[RQ] Failed to clear cache on impersonation start', { error: e })
    }
}

export async function resetQueryCacheOnImpersonationEnd(queryClient: QueryClient): Promise<void> {
    try {
        await queryClient.cancelQueries()
        // Add microtask delay to ensure cancellation completes before clearing
        await new Promise<void>((resolve) => queueMicrotask(() => resolve()))
        queryClient.clear()
        logger.info('[RQ] Cleared cache on impersonation end')
    } catch (e) {
        // Non-blocking safeguard; failures should not break navigation
        logger.warn('[RQ] Failed to clear cache on impersonation end', { error: e })
    }
}
